/**
 * Public share view: /forge-vision/r/:slug
 * Loads a previously computed Forge Vision analysis directly from the
 * 24h Mongo cache (no re-run, no auth) and renders a minimal share page.
 *
 * This is a real app-content route (renders backend data on a signed
 * slug), NOT marketing HTML — so it stays on the app subdomain.
 */
import React from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { marketingUrl } from "@/lib/marketingUrl";

export function ForgeVisionShare() {
    const { slug } = useParams();
    const [state, setState] = React.useState({ loading: true, data: null, error: null });
    React.useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/public/forge-vision/r/${slug}`);
                setState({ loading: false, data, error: null });
            } catch (err) {
                setState({ loading: false, data: null, error: err?.response?.data?.detail || "Not found" });
            }
        })();
    }, [slug]);

    if (state.loading) {
        return (
            <div className="text-arch-white bg-obsidian min-h-screen" data-testid="fv-share-loading">
                <section className="section pt-32">
                    <div className="container-editorial max-w-3xl">
                        <div className="chip chip-ai">Loading shared analysis</div>
                    </div>
                </section>
            </div>
        );
    }
    if (state.error) {
        return (
            <div className="text-arch-white bg-obsidian min-h-screen" data-testid="fv-share-notfound">
                <section className="section pt-32">
                    <div className="container-editorial max-w-3xl">
                        <div className="chip">Not found</div>
                        <h1 className="mt-6 font-display text-4xl">This share link is no longer available.</h1>
                        <p className="mt-4 text-arch-white/60">Shared analyses expire after 24 hours.</p>
                        <div className="mt-8">
                            <a href={marketingUrl("/")} className="bg-ice text-obsidian px-5 py-3 text-sm">Return to thedreamforgecompany.com</a>
                        </div>
                    </div>
                </section>
            </div>
        );
    }
    const r = state.data;
    return (
        <div className="text-arch-white bg-obsidian min-h-screen" data-testid="page-forge-vision-share">
            <section className="section pt-32">
                <div className="container-editorial">
                    <div className="chip chip-ai">Shared Forge Vision Analysis</div>
                    <h1 className="mt-6 font-display text-3xl md:text-5xl tracking-tight" data-testid="share-title">{r.query}</h1>
                    <p className="mt-4 text-sm text-arch-white/60">{r.demonstration_notice}</p>
                    <div className="mt-8 grid md:grid-cols-4 gap-3">
                        <div className="border border-arch-white/10 p-4"><div className="overline text-arch-white/40">Score</div><div className="mt-2 font-display text-2xl text-ice">{r.property.opportunity_score}</div></div>
                        <div className="border border-arch-white/10 p-4"><div className="overline text-arch-white/40">Highest & best use</div><div className="mt-2 text-sm">{r.property.highest_and_best_use}</div></div>
                        <div className="border border-arch-white/10 p-4"><div className="overline text-arch-white/40">Expected ROI</div><div className="mt-2 font-display text-2xl text-ice">{r.property.expected_roi_pct}%</div></div>
                        <div className="border border-arch-white/10 p-4"><div className="overline text-arch-white/40">Timeline (mo)</div><div className="mt-2 font-display text-2xl">{r.property.estimated_timeline_months}</div></div>
                    </div>
                    <div className="mt-8 border border-arch-white/10 p-6">
                        <div className="overline text-arch-white/40">AI Summary</div>
                        <p className="mt-2 text-sm text-arch-white/80 leading-relaxed">{r.narrative.summary}</p>
                    </div>
                    <div className="mt-10 flex gap-3">
                        <a href={marketingUrl("/")} className="bg-ice text-obsidian px-5 py-3 text-sm hover:bg-arch-white" data-testid="fv-share-cta-home">Visit thedreamforgecompany.com</a>
                    </div>
                </div>
            </section>
        </div>
    );
}

export default ForgeVisionShare;
